import * as React from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { Label } from "../../../../components/ui/label";
import { Button } from "../../../../components/ui/button";
import { YearMonthPicker } from "./YearMonthPicker";
import { OperationSelector } from "./OperationSelector";
import { SwitchInput } from "../SwitchInput";
import { DropdownInput } from "../DropdownInput";
import { TextAreaInput } from "../TextareaInput";
import { MotorCarrierType } from "./MotorCarrierType";
import { CorgiOperationsQuestions } from "./CorgiOperationsQuestions";

const ENTITY_TYPES = [
  { value: 'individual', label: 'Individual' },
  { value: 'corporation', label: 'Corporation' },
  { value: 'llc', label: 'LLC' },
  { value: 'joint_venture', label: 'Joint Venture' },
  { value: 'partnership', label: 'Partnership' },
];

const YES_NO_OPTIONS = [
  { value: "Y", label: "Yes" },
  { value: "N", label: "No" },
];

const AUTO_HAULER_COMMODITIES = new Set([
  "automobiles_5_vehicles_or_less",
  "automobiles_6_vehicles_or_more",
]);

export const INELIGIBLE = [
  "Ambulance, EMT, Mobile Blood Banks",
  "Armored Cars",
  "Arms, Ammunition, Fireworks",
  "Boat Haulers",
  "Automobile Dismantiers",
  "Boom Trucks",
  "Brokerage",
  "Cannabis Haulers",
  "Carníivais, Circus",
  "Cement Mixers, Pumpers",
  "Coal Haulers",
  "Contractors, Contractor Equipment Mobile Equipment",
  "Cotton Haulers",
  "Courier Services, magazines, news delivery, parcel, film delivery",
  "Residential Delivery",

  "Driving Schools",
  "Egg Haulers",
  "Fast food delivery, including restaurant take-out delivery",
  "Final Delivery",
  "Freight Brokerage",
  "Garbage/Refuse",
  "Hazmat, Nuclear, Biological",
  "Household Good Movers",
  "Livestock Transporters",
  "Logging Operations",
  "Milk Haulers",
  "Mobile cranes, Vehicles with buckets",
  "Sand, Gravel, Asphalt",
  "Mobile Home Toters",
  "Mobile Home Toters, Building Movers",

  "Municipal",
  "Operating outside of US",
  "Operations using double/triple trailers",
  "Oversized/Overweight haulers requiring pilot cars",
  "Owned Units, Rented or Leased to Others",
  "Private Passenger Vehícies",
  "Public Livery: Buses, taxis, black car, ride share, shuttle, day care, church",
  "Rental Leasing, Demo Vehícies",
  "Salvage, Scrap Metal",
  "Steel Dealers",
  "Tankers Hauling Flammable Liquids",
  "Towing/Repossession,",
  "Vendor Trucks",
  "Double or Triple Trailers",
];

const switchInputs = [
  {
    id: "prior-cancelled",
    stepName: "step3.priorCancelled",
    label:
      "Has your prior insurance ever been cancelled or non-renewed by any insurer in the past 3 years (for any reasons NOT related to payment of premium)?",
  },
  {
    id: "cancellations-12mo",
    stepName: "step3.cancellations12mo",
    label:
      "Does the insured have 4 or more policy cancellations within the past 12 months?",
  },
  {
    id: "uiia",
    stepName: "step3.uiiaIntermodal",
    label: "UIIA Intermodal?",
  },
  {
    id: "filingsAlFederal",
    stepName: "step3.filingsAlFederal",
    label: "Filings Al Federal?",
  },
  {
    id: "filingsAlState",
    stepName: "step3.filingsAlState",
    label: "Filings Al State?",
  },
  {
    id: "dump-tarped",
    stepName: "step3.dumpLoadsTarped",
    label: "Are all dump loads fully tarped?",
  },
  {
    id: "free-board",
    stepName: "step3.freeBoardSixInches",
    label: "Are drivers required to maintain 6 inches of free board?",
  },
  {
    id: "roadside-construction",
    stepName: "step3.roadsideConstruction",
    label:
      "Do any trucks load or unload cargo at roadside construction sites?",
  },
];

const arraysEqual = (a = [], b = []) =>
  a.length === b.length && a.every((v, i) => v === b[i]);

export function Operations() {
  const methods = useFormContext();
  const { control, watch, setValue, register } = methods;
  const [showList, setShowList] = React.useState(false);

  const selectedOps = watch("step3.operationTypes") || [];
  const noneMentioned = watch("step3.noneOfMentioned");
  const operatingStart = watch("step3.operatingStart");
  const commodities = useWatch({ control, name: "step8.commodity_array" }) || [];

  const step1Carrier = useWatch({ control, name: "step1.carrier" });
  const step2Carrier = useWatch({ control, name: "step2.carrier" });
  const step3Carrier = useWatch({ control, name: "step3.carrier" });
  const normalizedCarriers = React.useMemo(() => {
    const list = Array.isArray(step1Carrier)
      ? step1Carrier
      : step1Carrier ? [step1Carrier] : [];
    return list.map((s) => (s ?? ""));
  }, [step1Carrier]);
  const showTowingRepQuestion = commodities.some((commodity) =>
    AUTO_HAULER_COMMODITIES.has(commodity?.commodityKey)
  ) && normalizedCarriers.includes("Cover Whale");

  React.useEffect(() => {
    register("step2.carrier");
    register("step3.carrier");
  }, [register]);

  React.useEffect(() => {
    if (noneMentioned && selectedOps.length) {
      setValue("step3.operationTypes", [], { shouldDirty: true });
    }
  }, [noneMentioned, selectedOps.length, setValue]);

  React.useEffect(() => {
    if (!arraysEqual(normalizedCarriers, step2Carrier ?? [])) {
      setValue("step2.carrier", normalizedCarriers, { shouldDirty: false, shouldValidate: true });
    }
    if (!arraysEqual(normalizedCarriers, step3Carrier ?? [])) {
      setValue("step3.carrier", normalizedCarriers, { shouldDirty: false, shouldValidate: true });
    }
  }, [arraysEqual, normalizedCarriers, step2Carrier, step3Carrier, setValue]);

  React.useEffect(() => {
    if (!showTowingRepQuestion) {
      setValue("step3.opsTowingRep", undefined, { shouldDirty: true, shouldValidate: true });
    }
  }, [showTowingRepQuestion, setValue]);

  const hasProgressive = normalizedCarriers.includes("Progressive");
  const hasOtherCarriers = normalizedCarriers.some((c) => c && c !== "Progressive");
  const hasOperatingStart = Boolean(operatingStart?.year && operatingStart?.month);
  const showProgressiveWarning =
    hasProgressive && hasOtherCarriers && !hasOperatingStart;

  return (
    <div className="space-y-6">
      <h3 className="text-xl font-semibold">Operations</h3>

      <div className="rounded-lg p-0 space-y-4">
        {switchInputs.map(({ id, stepName, label }) => (
          <SwitchInput
            key={id}
            control={control}
            id={id}
            stepName={stepName}
            stepId="step3"
            label={label}
          />
        ))}

        {/* Time operating */}
        <YearMonthPicker
          label="Please select the month and year you began operating under your current DOT #"
          name="step3.operatingStart"
          step="step3"
        />
        {showProgressiveWarning && (
          <p className="text-sm text-red-600 px-1">
            Without a DOT number, you can only quote with Progressive. Clicking "Next" means we will be removing other carriers from this quote.
          </p>
        )}

        {/* Entity type */}
        <div className="space-y-2">
          <Label>What kind of entity is your business?</Label>
          <DropdownInput
            control={control}
            label={"Select"}
            dropdownLabel={"Select Entity Type"}
            stepName={"step3.entityType"}
            stepId={"step3"}
            itemList={ENTITY_TYPES}
          />
        </div>

        <OperationSelector control={control} methods={methods} watch={watch} />

        <CorgiOperationsQuestions control={control} />

        {showTowingRepQuestion && (
          <DropdownInput
            control={control}
            label={"Does the insured's operation include any towing, repossession or driveaway?"}
            dropdownLabel={"Select an answer"}
            stepName={"step3.opsTowingRep"}
            stepId={"step3"}
            itemList={YES_NO_OPTIONS}
          />
        )}

        <div className="text-red-600 font-semibold uppercase text-sm">
          See quote and policy form for complete list of exclusions
        </div>

        {/* Ineligible operations */}
        <div className="space-y-3">
          {/* <div className="flex items-start justify-start gap-4">
            
          </div> */}
          <div className="flex flex-col gap-2">
            <div className="flex gap-2 items-center">
              <Label htmlFor="has-ineligible" className="cursor-pointer block">
                Does insured conduct any of the following ineligible operations:
              </Label>
              <SwitchInput
                control={control}
                id="has-ineligible"
                stepName="step3.hasIneligibleOps"
                stepId="step3"
                className="mt-1"
              />
            </div>

            <Button
              variant="outline"
              onClick={() => setShowList((prev) => !prev)}
              className="mb-2 self-start"
            >
              {showList ? "Hide Complete Exclusion List" : "Show Complete Exclusion List"}
            </Button>

            {showList && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8">
                {INELIGIBLE.map((item) => (
                  <div
                    key={item}
                    className="text-sm text-gray-700 leading-relaxed"
                  >
                    • {item}
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>
        <TextAreaInput
          control={control}
          label={`Please provide a brief description of the Insured's operation.
            Please include what the insured is hauling.`}
          id={'ops-desc'}
          stepName={'step3.operationDescription'}
          stepId={'step3'}
          textAreaClassname={"min-h-[120px]"}
        />
        {step3Carrier && step3Carrier?.includes("Accelerant") && <MotorCarrierType />}
      </div>
    </div>
  );
}
